import { useCallback } from "react";
import { getUrlType } from "../utils/url-detection";
import { trackCtaClick } from "../utils/tag-manager";

interface UseCtaClickHandlerOptions {
    onVideoClick?: (videoUrl: string) => void;
    onDemoClick?: (demoUrl: string) => void;
}

/**
 * Hook for handling CTA button clicks
 * Automatically detects YouTube URLs and demo URLs and calls appropriate handlers
 */
export const useCtaClickHandler = ({ onVideoClick, onDemoClick }: UseCtaClickHandlerOptions = {}) => {
    const handleCtaClick = useCallback(
        (e: React.MouseEvent<HTMLElement>, href?: string) => {
            if (!href) {
                return;
            }

            const urlType = getUrlType(href);
            const target = e.currentTarget as HTMLElement;
            if (target.dataset.gtmTrack === "off") {
                return;
            }
            target.dataset.gtmTracked = "cta-hook";
            const label = target.textContent?.trim();
            const pagePath =
                typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : undefined;

            trackCtaClick({
                label,
                href,
                urlType,
                pagePath,
            });

            // Handle YouTube videos
            if (urlType === "youtube" && onVideoClick) {
                e.preventDefault();
                onVideoClick(href);
                return;
            }

            // Handle #video anchor as video trigger (opens video modal with default video)
            if (href === "#video" && onVideoClick) {
                e.preventDefault();
                onVideoClick("");
                return;
            }

            // Handle demo URLs
            if (urlType === "demo" && onDemoClick) {
                e.preventDefault();
                onDemoClick(href);
                return;
            }

            // Handle #demo anchor as demo trigger (opens demo modal with default demo)
            if (href === "#demo" && onDemoClick) {
                e.preventDefault();
                onDemoClick("");
                return;
            }

            // Handle #request-prototype anchor — opens the prototype-request modal,
            // which is mounted globally inside LandingPage and listens for this event.
            if (href === "#request-prototype" && typeof window !== "undefined") {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("request-prototype:open"));
                return;
            }

            // Handle #workshop-inquiry anchor — opens the workshop-inquiry modal,
            // which each of the four workshop detail pages mounts and scopes to its
            // own inquiryType.
            if (href === "#workshop-inquiry" && typeof window !== "undefined") {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("workshop-inquiry:open"));
                return;
            }

            // For internal links starting with #, handle smooth scrolling
            if (href.startsWith("#")) {
                e.preventDefault();
                const targetId = href.substring(1);
                const targetElement = document.getElementById(targetId);
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                return;
            }

            // Open external URLs in a new tab (noopener,noreferrer for security)
            if (urlType === "external") {
                e.preventDefault();
                window.open(href, "_blank", "noopener,noreferrer");
                return;
            }

            // For internal relative links, let the default behavior happen (navigation)
        },
        [onVideoClick, onDemoClick]
    );

    return { handleCtaClick };
};
